import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

// The full demo-license issuance flow runs through the existing
// ScheduleSessionWizard now (see IssueDemoLauncher → ScheduleSessionWizard).
// The previous bespoke `useIssueDemoLicense` mutation was removed in favor
// of `useGetOrMintLicense({ demoOpts })` invoked from useScheduleSession.
//
// This file is now solely a count helper for the launcher's cap badge.

/**
 * Lightweight count for the "X of N demo licenses active" badge shown in
 * IssueDemoLauncher. Server-side cap enforcement still lives in
 * `trg_neurax_demo_cap`; this is purely for UX.
 */
export function useActiveDemoLicenseCount() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'demo-license-count', companyId],
    queryFn: async (): Promise<{ count: number; cap: number }> => {
      const today = new Date().toISOString().split('T')[0];

      // Demos point at b2b_end_clients (with is_demo=true) per the symmetry
      // trigger, so we count by joining through end_clients to get the company.
      const [{ data: licRows, count }, { data: company }] = await Promise.all([
        supabase
          .from('neurax_licenses')
          .select('id, b2b_end_clients!inner(b2b_company_id)', { count: 'exact' })
          .eq('license_type', 'employee_demo')
          .eq('status', 'active')
          .or(`valid_until.is.null,valid_until.gt.${today}`)
          .eq('b2b_end_clients.b2b_company_id', companyId),
        supabase.from('b2b_companies').select('max_active_demos').eq('id', companyId).single(),
      ]);

      return {
        count: count ?? licRows?.length ?? 0,
        cap: (company as { max_active_demos: number } | null)?.max_active_demos ?? 5,
      };
    },
    staleTime: 10_000,
  });
}
